import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  getHearingById,
  hearingsSelector,
  isAllHearingsDisconnectedSelector,
  selectIsOutboundHearingCall,
  selectIsVriOrIsVrsInboundHearingCall,
} from './callHearingSelectors';
import { setReceivedPrimaryHearing } from './callHearingSlice';
import { adaptIncomingPrimaryHearing } from './callHearingAdapter';

import type { RootState } from 'features/app/store/store';
import { ParticipantsStatusEventBus } from 'features/call/call-status/services';
import {
  isTeamingInProgressSelector,
  selectIsAcceptingUserAndCurrentlyTeaming,
} from 'features/teaming/teaming-base/store';
import { VoiceMeetingEventBus } from 'features/voice-meeting/services';
import type { VoiceSessionParticipantEvent } from 'features/voice-session/interfaces';
import {
  isHearingToDeafSelector,
  callIdSelector,
} from 'features/call/call-base/store/selectors';
import { handleError } from 'features/notification/store';
import type { Call } from 'features/call/call-base/interfaces';
import { voiceSessionIdSelector } from 'features/voice-session/store';
import { isVrsModeSelector } from 'features/multi-mode/store';
import { resetAgentDisconnectHearing } from 'features/reporting/store';

export const handleHearingDeclinedCall = createAsyncThunk(
  'call-hearing/disconnectedFromCall',
  async (payload: VoiceSessionParticipantEvent, { getState }) => {
    const state = getState() as RootState;
    const hearing = getHearingById(state, payload.participantId);
    const isTeaming = isTeamingInProgressSelector(state);

    if (hearing?.status === 'connecting') {
      VoiceMeetingEventBus.hearingCallDeclined$.next({
        phoneNumber: hearing.phoneNumber,
        disconnectReason: payload.disconnectedReason,
      });
    }

    if (hearing?.status === 'connected') {
      const hearings = hearingsSelector(state);
      const isSomeHearingConnected = hearings.some(
        (participant) =>
          participant.status === 'connected' && participant.id !== hearing.id
      );

      if (!isSomeHearingConnected && !isTeaming) {
        VoiceMeetingEventBus.finishVoiceSession$.next();
      }

      ParticipantsStatusEventBus.hearing.$disconnected.next({
        phoneNumber: hearing.phoneNumber,
        disconnectReason: payload.disconnectedReason,
        id: payload.participantId,
      });
    }
  }
);

export const primaryHearingReceived = createAsyncThunk(
  'callHearing/primaryHearingReceived',
  async (call: Call, { dispatch, getState }) => {
    const state = getState() as RootState;
    const isHearingToDeaf = isHearingToDeafSelector(state);
    try {
      const hearingToAdd = adaptIncomingPrimaryHearing(call, isHearingToDeaf);
      dispatch(setReceivedPrimaryHearing(hearingToAdd));
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'primaryHearingReceived',
          title: 'Failed to update primary hearing',
        })
      );
      throw error;
    }
  }
);

/**
 * Disconnects a hearing participant by ID.
 */
export const disconnectHearingByIdForUI = createAsyncThunk(
  'call-hearing/disconnectById',
  async (id: string, { getState, dispatch }) => {
    try {
      const state = getState() as RootState;
      const hearings = hearingsSelector(state);

      // Find participant by ID to get the phone number
      const participant = hearings.find((h) => h.id === id);

      // Get the phone number from the participant if available
      const phoneNumber = participant?.phoneNumber || '';

      if (!phoneNumber) {
        console.warn(
          `No phone number found for hearing participant ID: ${id}, using empty string`
        );
      }

      // Notify the system about the disconnection
      ParticipantsStatusEventBus.hearing.$disconnected.next({
        id,
        phoneNumber,
        disconnectReason: 'disconnected by agent',
      });

      return { id, phoneNumber };
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'disconnectHearingByIdOrPhoneNumber',
          title: 'Failed to disconnect hearing participant',
        })
      );
      throw error;
    }
  }
);

/* Gets the current state values needed for the HearingsDisconnectListener component
 * to avoid stale closures.
 */
export const getHearingsSessionState = createAsyncThunk(
  'callHearing/getHearingsSessionState',
  async (_, { getState }) => {
    const state = getState() as RootState;

    return {
      voiceSessionId: voiceSessionIdSelector(state),
      isAcceptingUserAndCurrentlyTeaming:
        selectIsAcceptingUserAndCurrentlyTeaming(state),
      isAllHearingsDisconnected: isAllHearingsDisconnectedSelector(state),
      callId: callIdSelector(state),
      isOutboundVrsHearingCall:
        selectIsOutboundHearingCall(state) && isVrsModeSelector(state),
      isVriOrIsVrsInboundHearingCall:
        selectIsVriOrIsVrsInboundHearingCall(state),
    };
  }
);

/**
 * Notifies the system that a participant has been disconnected.
 */
export const notifyParticipantDisconnected = createAsyncThunk(
  'callHearing/notifyParticipantDisconnected',
  async (
    payload: { phoneNumber: string; id: string },
    { dispatch, getState }
  ) => {
    const state = getState() as RootState;
    const didAgentDisconnectHearing = state.reporting.agentDisconnectHearing;
    try {
      ParticipantsStatusEventBus.hearing.$disconnected.next({
        phoneNumber: payload.phoneNumber,
        id: payload.id,
        disconnectReason: didAgentDisconnectHearing
          ? 'disconnected by agent'
          : undefined,
      });

      dispatch(resetAgentDisconnectHearing());

      return payload;
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'notifyParticipantDisconnected',
          title: 'Failed to notify participant disconnection',
        })
      );
      throw error;
    }
  }
);
